import * as React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { AuthProvider } from "react-oidc-context";
import App from "./App";
import { User } from "oidc-client-ts";
import "./tailwind.css";
import { getRedirectUri, isNativePlatform } from "./utils/mobileAuth";
import { PlatformAuthProvider } from "./contexts/AuthContext";
import { StatusBar } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import { getBrandName, isKineticNotesAccount, isBestiHealthAccount } from "./utils/brandUtils";

// Validate required environment variables
if (!import.meta.env.VITE_REDIRECT_URI) {
  console.error("VITE_REDIRECT_URI is not set!");
  throw new Error("VITE_REDIRECT_URI environment variable is required");
}

if (!import.meta.env.VITE_COGNITO_CLIENT_ID) {
  console.error("VITE_COGNITO_CLIENT_ID is not set!");
  throw new Error("VITE_COGNITO_CLIENT_ID environment variable is required");
}

// Set dynamic page title based on brand
document.title = getBrandName();

// Set dynamic favicon based on brand
const setFavicon = (iconPath: string) => {
  // Remove existing favicon links
  const existingLinks = document.querySelectorAll("link[rel~='icon']");
  existingLinks.forEach(link => link.remove());
  
  // Add 16px favicon (primary, for browser tabs)
  const link16 = document.createElement("link");
  link16.rel = "icon";
  link16.type = "image/png";
  link16.sizes = "16x16";
  link16.href = iconPath;
  document.head.appendChild(link16);
  
  // Add 32px favicon (for better quality on some browsers/platforms)
  const iconPath32 = iconPath.replace("-16.png", "-32.png");
  const link32 = document.createElement("link");
  link32.rel = "icon";
  link32.type = "image/png";
  link32.sizes = "32x32";
  link32.href = iconPath32;
  document.head.appendChild(link32);
};

if (isKineticNotesAccount()) {
  setFavicon("/kinetic-scribe-logo-16.png");
} else if (isBestiHealthAccount()) {
  setFavicon("/besti_fav.png");
}

const userPoolIdSuffix = import.meta.env.VITE_COGNITO_USER_POOL_ID;

// Get the appropriate redirect URI based on platform
const webRedirectUri = import.meta.env.VITE_REDIRECT_URI;
const redirectUri = getRedirectUri(webRedirectUri);

console.log("Platform:", isNativePlatform() ? "Native (iOS/Android)" : "Web");
console.log("Redirect URI:", redirectUri);

// For web: use current origin's callback
// For native: use custom URL scheme
const webRedirect = `${window.location.origin}/callback`;
const redirect_uri = isNativePlatform() ? redirectUri : webRedirect;
const silent_redirect = `${window.location.origin}/callback`;

// Validate VITE_COGNITO_DOMAIN is set (used for manual logout in AuthContext)
if (!import.meta.env.VITE_COGNITO_DOMAIN) {
  console.error("VITE_COGNITO_DOMAIN is not set!");
  throw new Error("VITE_COGNITO_DOMAIN environment variable is required");
}

const oidc = {
  authority: `https://cognito-idp.us-east-1.amazonaws.com/us-east-1_${userPoolIdSuffix}`,
  client_id: import.meta.env.VITE_COGNITO_CLIENT_ID,
  redirect_uri: redirect_uri,
  silent_redirect_uri: silent_redirect,
  post_logout_redirect_uri: isNativePlatform() ? redirectUri : `${window.location.origin}`,
  response_type: `code`,
  scope: `openid email phone`,
  loadUserInfo: true,
  automaticSilentRenew: !isNativePlatform(),

  onSigninCallback: (user: User | void) => {
    if (user?.state) localStorage.setItem("accountType", user.state as string);
    /* Remove ?code=…&state=… from the address bar (web only) */
    if (!isNativePlatform()) {
      window.history.replaceState({}, document.title, "/");
    }
  },
};

console.log("OIDC Config:", oidc);
console.log("Redirect URI selected:", redirectUri);

// Prevent status bar from overlaying the app on native platforms
if (Capacitor.isNativePlatform()) {
  StatusBar.setOverlaysWebView({ overlay: false }).catch(err => 
    console.error('StatusBar error:', err)
  );
}

// Conditionally wrap with web OIDC provider only on web
const AppWithAuth = isNativePlatform() ? (
  // iOS: Use only PlatformAuthProvider (native OAuth)
  <PlatformAuthProvider>
    <App />
  </PlatformAuthProvider>
) : (
  // Web: Use both AuthProvider and PlatformAuthProvider
  <AuthProvider {...oidc}>
    <PlatformAuthProvider>
      <App />
    </PlatformAuthProvider>
  </AuthProvider>
);

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <React.StrictMode>
    <BrowserRouter>
      {AppWithAuth}
    </BrowserRouter>
  </React.StrictMode>
);
